import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js"
import { collectAddOnSelections } from "./add_on_serializer"

// Stripe Payment Element on the reservation form (migrated from the legacy Card
// Element). Uses the deferred-PaymentIntent pattern: the Payment Element mounts
// with mode/amount (no client secret), the firm-balance PI is created on submit,
// then confirmed. The server places the refundable deposit off-session.
export default class extends Controller {
  static targets = [
    "paymentElement",  // mount point for the Payment Element
    "cardError",       // error message container
    "rentalPiField",   // hidden: rental_payment_intent_id
    "submitBtn",
    "checkInDate",
    "checkOutDate",
    "guestCount",
  ]
  static values = {
    propertyId: Number,
    paymentIntentUrl: String,
    initialAmount: Number,   // firm-total cents for the initial Element mount (> 0)
  }

  async connect() {
    const key = document.querySelector('meta[name="stripe-key"]')?.content
    if (!key || !this.hasPaymentElementTarget) return // Stripe not configured

    this.stripe = await loadStripe(key)
    this.elements = this.stripe.elements({
      mode: "payment",
      amount: Math.max(this.initialAmountValue || 100, 100),
      currency: "usd",
      captureMethod: "manual",
      setupFutureUsage: "off_session",
      paymentMethodTypes: ["card"], // card only; MUST match the server PI's payment_method_types
      // Dark theme to match the site (Stripe's built-in night base + our palette).
      appearance: {
        theme: "night",
        variables: {
          colorPrimary: "#6f9e4f",
          colorBackground: "#26292b",
          colorText: "#dedcd9",
          colorTextSecondary: "#a8a6a2",
          colorDanger: "#dc969d",
          borderRadius: "6px",
          fontFamily: "system-ui, sans-serif",
        },
      },
    })
    this.paymentElement = this.elements.create("payment", {
      fields: { billingDetails: "auto" },
      wallets: { applePay: "never", googlePay: "never" }, // card form only
    })
    this.paymentElement.mount(this.paymentElementTarget)

    // The reservation-form controller fetches the authoritative Pricing::Quote
    // and broadcasts the firm total; keep the Element amount in sync with it
    // (display + payment-method eligibility — the server PI is authoritative on
    // the real charge).
    this.element.addEventListener("reservation-form:quoted", this.#onQuoted)
  }

  disconnect() {
    this.element.removeEventListener("reservation-form:quoted", this.#onQuoted)
  }

  #onQuoted = (event) => {
    const cents = event.detail?.amountCents
    if (this.elements && cents > 0) this.elements.update({ amount: cents })
  }

  async submit(event) {
    if (!this.stripe || !this.elements) return // no Stripe — plain submit
    event.preventDefault()

    const checkIn = this.hasCheckInDateTarget ? this.checkInDateTarget.value : null
    const checkOut = this.hasCheckOutDateTarget ? this.checkOutDateTarget.value : null
    if (!checkIn || !checkOut) {
      this.#showError("Please select check-in and check-out dates first.")
      return
    }

    this.#busy(true)
    try {
      // 1. Validate the Payment Element.
      const { error: submitError } = await this.elements.submit()
      if (submitError) { this.#showError(submitError.message); return }

      // 2. Create the firm-balance PaymentIntent on the server.
      const res = await fetch(this.paymentIntentUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          property_id: this.propertyIdValue,
          check_in_date: checkIn,
          check_out_date: checkOut,
          guest_count: this.hasGuestCountTarget ? this.guestCountTarget.value : 1,
          add_ons: collectAddOnSelections(this.element),
          // Must mirror the /quote payload — otherwise the PI is built for the
          // cheapest package and create()'s anti-tamper check rejects the charge.
          packages: this.#collectPackages(),
          trophy: this.#collectTrophy(),
        }),
      })
      const data = await res.json()
      if (data.error || !data.client_secret) { this.#showError(data.error || "Payment setup failed."); return }

      // 3. Confirm. Card-only (paymentMethodTypes:["card"]) + redirect:"if_required" => no redirect.
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: data.client_secret,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      })
      if (error) { this.#showError(error.message); return }

      if (this.hasRentalPiFieldTarget) this.rentalPiFieldTarget.value = paymentIntent.id
      this.element.submit()
    } catch (e) {
      this.#showError("An unexpected error occurred. Please try again.")
    }
  }

  // role => chosen PropertyLodgingPackage id, from the package-picker radios.
  #collectPackages() {
    const out = {}
    this.element.querySelectorAll('input[name^="packages["]:checked').forEach((el) => {
      const m = el.name.match(/packages\[(.+)\]/)
      if (m) out[m[1]] = el.value
    })
    return out
  }

  // { "<pat_id>": { quantity, tier } } from the trophy-picker inputs.
  #collectTrophy() {
    const out = {}
    this.element.querySelectorAll('input[name^="trophy["]').forEach((el) => {
      const m = el.name.match(/trophy\[(.+?)\]\[quantity\]/)
      if (!m) return
      if (!parseInt(el.value, 10)) return
      const tier = this.element.querySelector(`select[name="trophy[${m[1]}][tier]"]`)
      out[m[1]] = { quantity: el.value, tier: tier ? tier.value : undefined }
    })
    return out
  }

  #busy(on) {
    if (!this.hasSubmitBtnTarget) return
    this.submitBtnTarget.disabled = on
    this.submitBtnTarget.textContent = on ? "Processing payment..." : "Submit Reservation Request"
  }

  #showError(message) {
    if (this.hasCardErrorTarget) this.cardErrorTarget.textContent = message
    this.#busy(false)
  }
}
